import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowUpRight, CheckCircle2, AlertCircle } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPICS = [
  "Ministry / Government Partnership",
  "Institutional Collaboration",
  "Press & Media",
  "Investor / Partnerships",
  "General Enquiry",
];

/**
 * Inline enquiry form section — posts to /api/enquiries which persists to
 * MongoDB and triggers a transactional email to contact@replayera.com.
 */
export default function InlineEnquiryForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organisation: "",
    topic: TOPICS[0],
    message: "",
  });
  const [state, setState] = useState({ status: "idle", error: "" });
  // status: idle | submitting | success | error

  const update = (k) => (e) => setForm((v) => ({ ...v, [k]: e.target.value }));

  const canSubmit =
    form.name.trim() && form.email.trim() && form.message.trim() && state.status !== "submitting";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setState({ status: "submitting", error: "" });
    try {
      const res = await axios.post(`${API}/enquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        organisation: form.organisation.trim() || null,
        topic: form.topic,
        message: form.message.trim(),
      });
      if (res.data && res.data.status === "received") {
        setState({ status: "success", error: "" });
      } else {
        setState({ status: "error", error: "Unexpected response from server." });
      }
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";
      setState({ status: "error", error: String(detail) });
    }
  };

  const reset = () => {
    setForm({
      name: "",
      email: "",
      organisation: "",
      topic: TOPICS[0],
      message: "",
    });
    setState({ status: "idle", error: "" });
  };

  return (
    <section
      id="contact"
      data-testid="contact-form-section"
      className="relative py-20 md:py-28 border-t border-gold/10 bg-obsidian"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-start">
          {/* Left rail — headline & copy */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 font-mono-accent text-[11px] text-gold/80">
              <span>Chapter 08</span>
              <span className="w-8 h-px bg-gold/40" />
              <span className="text-bone/50">Get in touch</span>
            </div>
            <h2
              className="mt-6 font-display text-5xl md:text-7xl leading-[0.95] text-bone tracking-tight"
              data-testid="contact-heading"
            >
              Contact us
              <br />
              <em className="text-gold not-italic">for enquiries.</em>
            </h2>
            <p className="mt-6 text-bone/70 text-lg leading-relaxed max-w-md">
              Ministry contacts, institutional partners, press, investors — tell
              us what you&rsquo;re building or curious about, and we&rsquo;ll be
              in touch shortly.
            </p>
            <div className="mt-8 font-mono-accent text-[10px] text-bone/50">
              Or reply directly at{" "}
              <a
                href="mailto:contact@replayera.com"
                className="text-gold hover:underline"
                data-testid="contact-mailto"
              >
                contact@replayera.com
              </a>
            </div>
          </div>

          {/* Right rail — form or success state */}
          <div className="md:col-span-7 relative">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.9, ease: EASE }}
              className="border border-gold/20 bg-midnight p-6 md:p-10"
            >
              {state.status === "success" ? (
                <div data-testid="contact-success" className="py-4">
                  <div className="flex items-center gap-3 text-gold">
                    <CheckCircle2 className="w-6 h-6" strokeWidth={1.5} />
                    <span className="font-mono-accent text-[11px]">
                      Enquiry received
                    </span>
                  </div>
                  <h3 className="mt-4 font-display text-3xl md:text-4xl text-bone leading-tight">
                    Thank you.
                    <br />
                    <em className="text-gold not-italic">
                      We&rsquo;ll be in touch shortly.
                    </em>
                  </h3>
                  <p className="mt-4 text-bone/70 leading-relaxed max-w-lg">
                    A confirmation has been logged to our system and forwarded
                    to the ReplayEra team. Expect a reply from{" "}
                    <span className="text-gold">contact@replayera.com</span>.
                  </p>
                  <button
                    type="button"
                    onClick={reset}
                    data-testid="contact-send-another"
                    className="mt-8 inline-flex items-center gap-2 border border-gold/40 text-bone hover:border-gold hover:text-gold px-5 py-2.5 rounded-full font-mono-accent text-[11px] transition-colors"
                  >
                    Send another enquiry
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="space-y-5"
                  data-testid="contact-form"
                  noValidate
                >
                  <div className="grid md:grid-cols-2 gap-5">
                    <label className="block">
                      <span className="font-mono-accent text-[10px] text-bone/60">
                        Name
                      </span>
                      <input
                        type="text"
                        required
                        value={form.name}
                        onChange={update("name")}
                        data-testid="contact-name"
                        className="mt-2 w-full bg-transparent border-b border-gold/25 focus:border-gold outline-none py-2 text-bone placeholder:text-bone/25 font-display text-lg transition-colors"
                        placeholder="Your name"
                      />
                    </label>
                    <label className="block">
                      <span className="font-mono-accent text-[10px] text-bone/60">
                        Email
                      </span>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={update("email")}
                        data-testid="contact-email"
                        className="mt-2 w-full bg-transparent border-b border-gold/25 focus:border-gold outline-none py-2 text-bone placeholder:text-bone/25 font-display text-lg transition-colors"
                        placeholder="you@domain.com"
                      />
                    </label>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <label className="block">
                      <span className="font-mono-accent text-[10px] text-bone/60">
                        Organisation
                      </span>
                      <input
                        type="text"
                        value={form.organisation}
                        onChange={update("organisation")}
                        data-testid="contact-organisation"
                        className="mt-2 w-full bg-transparent border-b border-gold/25 focus:border-gold outline-none py-2 text-bone placeholder:text-bone/25 font-display text-lg transition-colors"
                        placeholder="Optional"
                      />
                    </label>
                    <label className="block">
                      <span className="font-mono-accent text-[10px] text-bone/60">
                        Topic
                      </span>
                      <select
                        value={form.topic}
                        onChange={update("topic")}
                        data-testid="contact-topic"
                        className="mt-2 w-full bg-midnight border border-gold/25 focus:border-gold outline-none py-3 px-3 text-bone font-mono-accent text-[11px] cursor-pointer transition-colors"
                      >
                        {TOPICS.map((t) => (
                          <option key={t} value={t} className="bg-midnight text-bone">
                            {t}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <label className="block">
                    <span className="font-mono-accent text-[10px] text-bone/60">
                      Message
                    </span>
                    <textarea
                      required
                      rows={5}
                      value={form.message}
                      onChange={update("message")}
                      data-testid="contact-message"
                      className="mt-2 w-full bg-transparent border border-gold/20 focus:border-gold outline-none py-3 px-4 text-bone placeholder:text-bone/25 leading-relaxed transition-colors resize-none"
                      placeholder="Tell us about your interest, institution or partnership idea…"
                    />
                  </label>

                  {state.status === "error" && (
                    <div
                      data-testid="contact-error"
                      className="flex items-center gap-2 border border-red-400/40 bg-red-500/5 text-red-200 px-4 py-3 font-mono-accent text-[10px]"
                    >
                      <AlertCircle className="w-4 h-4" strokeWidth={1.5} />
                      <span>{state.error}</span>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-2 gap-4 flex-wrap">
                    <span className="font-mono-accent text-[10px] text-bone/40">
                      Replies from{" "}
                      <span className="text-gold">contact@replayera.com</span>
                    </span>
                    <button
                      type="submit"
                      disabled={!canSubmit}
                      data-testid="contact-submit"
                      className="group inline-flex items-center gap-3 bg-gold text-obsidian disabled:bg-gold/30 disabled:cursor-not-allowed hover:bg-bone px-6 py-3 rounded-full font-mono-accent text-[11px] transition-colors"
                    >
                      {state.status === "submitting" ? "Sending…" : "Send enquiry"}
                      <ArrowUpRight
                        className="w-4 h-4 group-hover:rotate-45 transition-transform"
                        strokeWidth={1.6}
                      />
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
